import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import { canPerformAction, isModuleEnabled } from "../../utils/permissions";

/**
 * PermissionGuard component to conditionally render content based on user permissions
 *
 * @param {Object} props
 * @param {string} props.module - The module name to check permissions for
 * @param {string} props.action - The specific action to check (optional)
 * @param {React.ReactNode} props.children - The content to render if permission is granted
 * @param {React.ReactNode} props.fallback - Optional fallback content to show if permission is denied
 * @param {boolean} props.requireModuleEnabled - Whether to require the module to be enabled (default: true)
 * @returns {React.ReactNode}
 */
const PermissionGuard = ({
  module,
  action,
  children,
  fallback = null,
  requireModuleEnabled = true,
  showMessage = false,
  message = "You are not allowed to access this page",
}) => {
  const { permissions } = useAuth();

  // If no module specified, always render children
  if (!module) {
    return children;
  }

  let hasPermission = false;

  if (action) {
    // Check specific action permission
    hasPermission = canPerformAction(permissions, module, action);
  } else if (requireModuleEnabled) {
    // Check if module is enabled
    hasPermission = isModuleEnabled(permissions, module);
  } else {
    // Just check if module exists in permissions
    hasPermission = permissions && permissions[module];
  }

  return hasPermission ? children : fallback || (showMessage && message);
};

export default PermissionGuard;

/**
 * Hook to check permissions programmatically
 * @param {string} module - Module name
 * @param {string} action - Action to check (optional)
 * @param {boolean} requireModuleEnabled - Whether to require module to be enabled
 * @returns {boolean} - Whether user has permission
 */
export const usePermission = (module, action, requireModuleEnabled = true) => {
  const { permissions } = useAuth();

  if (!module) {
    return true;
  }

  if (action) {
    return canPerformAction(permissions, module, action);
  } else if (requireModuleEnabled) {
    return isModuleEnabled(permissions, module);
  } else {
    return permissions && permissions[module];
  }
};
